import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import "./Careers.css";
import ApplyModal from "./ApplyModal";

// Helper to convert filename to URL slug
export const getJobSlug = (filename, index) => {
    if (!filename) return `job-${index !== undefined && index !== null ? index + 1 : '1'}`;
    return filename
        .replace(/\.[^/.]+$/, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
};

export default function Careers() {
    const { jobSlug } = useParams();

    // Dynamically load image filenames from the careers manifest
    const [images, setImages] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [zoomLevel, setZoomLevel] = useState(0); // 0 means fit to screen
    const [applyState, setApplyState] = useState({ isOpen: false, jobImage: null, jobIndex: null });
    const [copiedCardIndex, setCopiedCardIndex] = useState(null);

    useEffect(() => {
        fetch(`${process.env.PUBLIC_URL}/career-assets/manifest.json`)
            .then(res => res.json())
            .then(data => {
                const imgList = data.images || [];
                setImages(imgList);

                // Deep linking check: if user navigated directly to /careers/:jobSlug
                if (jobSlug && imgList.length > 0) {
                    const matchIndex = imgList.findIndex((img, idx) => getJobSlug(img, idx) === jobSlug.toLowerCase());
                    if (matchIndex !== -1) {
                        setApplyState({
                            isOpen: true,
                            jobImage: imgList[matchIndex],
                            jobIndex: matchIndex
                        });
                    }
                }
            })
            .catch(err => console.error('Failed to load careers manifest:', err));
    }, [jobSlug]);

    useEffect(() => {
        if (selectedIndex !== null || applyState.isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [selectedIndex, applyState.isOpen]);

    const openApplyModal = (e, index, img) => {
        if (e) e.stopPropagation();
        setApplyState({
            isOpen: true,
            jobImage: img || (index !== null ? images[index] : null),
            jobIndex: index
        });
    };

    const closeApplyModal = () => {
        setApplyState({ isOpen: false, jobImage: null, jobIndex: null });
    };

    const copyCardShareUrl = async (e, index, img) => {
        e.stopPropagation();
        const slug = getJobSlug(img, index);
        const url = `${window.location.origin}/careers/${slug}`;

        // 1. Try Mobile Native Social Media / App Share Sheet if supported
        if (navigator.share) {
            try {
                await navigator.share({
                    url
                });
                return; // Shared successfully via mobile OS share dialog
            } catch (err) {
                // If user didn't cancel the share dialog, fallback to copying link
                if (err.name === 'AbortError') return;
            }
        }

        // 2. Fallback to Clipboard copy
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(url).then(() => {
                setCopiedCardIndex(index);
                setTimeout(() => setCopiedCardIndex(null), 2500);
            }).catch(() => {});
        }
    };

    const openModal = (index) => {
        setSelectedIndex(index);
        setZoomLevel(0);
    };
    
    const closeModal = () => {
        setSelectedIndex(null);
        setZoomLevel(0);
    };

    const goToPrevious = (e) => {
        e.stopPropagation();
        setSelectedIndex((prevIndex) => (prevIndex === 0 ? images.length - 1 : prevIndex - 1));
    };

    const goToNext = (e) => {
        e.stopPropagation();
        setSelectedIndex((prevIndex) => (prevIndex === images.length - 1 ? 0 : prevIndex + 1));
    };

    const zoomIn = (e) => {
        e.stopPropagation();
        if (zoomLevel === 0) setZoomLevel(100);
        else setZoomLevel((prev) => Math.min(prev + 20, 100));
    };

    const zoomOut = (e) => {
        e.stopPropagation();
        if (zoomLevel === 0) return; // Already at default
        setZoomLevel((prev) => {
            const next = prev - 20;
            return next < 40 ? 0 : next; // Snap back to Fit to Screen if zooming out too much
        });
    };

    const fitToScreen = (e) => {
        e.stopPropagation();
        setZoomLevel(0);
    };

    return (
        <>
            <section 
                className="careers-full-page" 
                style={{ 
                    backgroundImage: `url(${require('../../assets/images/banner.jpg').default || require('../../assets/images/banner.jpg')})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundAttachment: 'fixed', /* Creates a nice parallax effect */
                    minHeight: '100vh',
                    position: 'relative',
                    paddingTop: '100px', /* Reduced gap below the header */
                    paddingBottom: '80px'
                }}
            >
                {/* Dark overlay to make content pop */}
                <div style={{
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.65)',
                    zIndex: 0
                }}></div>

                <div className="container" style={{ position: 'relative', zIndex: 1 }}>
                    <div className="section-title text-center mb-50">
                        <h2 className="text-white" style={{ fontSize: '42px', fontWeight: 'bold' }}>Join Our Engineering Team</h2>
                        <p className="mt-3" style={{ maxWidth: '600px', margin: '0 auto', color: 'rgba(255,255,255,0.85)', fontSize: '18px' }}>
                            We are constantly looking for talented individuals who are passionate about building scalable, impactful software solutions. 
                        </p>
                    </div>
                    
                    {images.length === 0 ? (
                        <div className="text-center mt-50 mb-50 empty-state-glass" style={{
                            background: 'rgba(255,255,255,0.1)',
                            backdropFilter: 'blur(10px)',
                            padding: '40px',
                            borderRadius: '20px',
                            border: '1px solid rgba(255,255,255,0.2)',
                            maxWidth: '600px',
                            margin: '0 auto'
                        }}>
                            <i className="fas fa-rocket fa-4x mb-4 text-white"></i>
                            <h3 className="text-white" style={{ fontWeight: 'bold' }}>No Open Roles Right Now</h3>
                            <p className="mt-3" style={{ fontSize: '18px', color: 'rgba(255,255,255,0.8)' }}>
                                We've recently filled our open positions, but we are always excited to connect with talented people. Please check back soon!
                            </p>
                        </div>
                    ) : (
                        <div className="row justify-content-center careers-gallery">
                            {images.map((img, index) => {
                                const imgSrc = `${process.env.PUBLIC_URL}/career-assets/${img}`;
                                return (
                                    <div className="col-lg-4 col-md-6 mb-40" key={index}>
                                        <div 
                                            className="career-card" 
                                            onClick={() => openModal(index)}
                                            style={{
                                                background: 'rgba(255,255,255,0.1)',
                                                backdropFilter: 'blur(10px)',
                                                border: '1px solid rgba(255,255,255,0.2)'
                                            }}
                                        >
                                            <img src={imgSrc} alt={`Job Advertisement ${index + 1}`} />
                                            <div className="overlay">
                                                <div className="card-actions-row">
                                                    <button 
                                                        type="button" 
                                                        className="card-apply-btn"
                                                        onClick={(e) => openApplyModal(e, index, img)}
                                                    >
                                                        <i className="fas fa-paper-plane mr-2"></i> Apply Now
                                                    </button>
                                                    <div className="view-btn">
                                                        <i className="fas fa-search-plus mr-2"></i> Preview
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className={`card-share-btn ${copiedCardIndex === index ? 'copied' : ''}`}
                                                        onClick={(e) => copyCardShareUrl(e, index, img)}
                                                        title="Copy share link for this job"
                                                    >
                                                        <i className={`fas ${copiedCardIndex === index ? 'fa-check' : 'fa-share-alt'}`}></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Lightbox Modal */}
                {selectedIndex !== null && (
                    <div className="career-modal-backdrop" onClick={closeModal}>
                        <div 
                            className="career-modal-content" 
                            onClick={(e) => e.stopPropagation()}
                            style={{ 
                                width: zoomLevel === 0 ? 'auto' : `${(zoomLevel / 100) * 1200}px`, 
                                transition: 'width 0.3s ease' 
                            }}
                        >
                            <button 
                                type="button" 
                                className="lightbox-apply-btn"
                                onClick={(e) => {
                                    const curIdx = selectedIndex;
                                    const curImg = images[curIdx];
                                    closeModal();
                                    openApplyModal(e, curIdx, curImg);
                                }}
                            >
                                <i className="fas fa-paper-plane mr-2"></i> Apply for this Role
                            </button>

                            <button type="button" className="career-modal-close" onClick={closeModal}>
                                <i className="fas fa-times"></i>
                            </button>
                            
                            <div className="career-modal-zoom-controls">
                                <button type="button" onClick={zoomOut} title="Zoom Out"><i className="fas fa-search-minus"></i></button>
                                <button type="button" onClick={fitToScreen} title="Fit to Screen"><i className="fas fa-expand"></i></button>
                                <button type="button" onClick={zoomIn} title="Zoom In"><i className="fas fa-search-plus"></i></button>
                            </div>

                            {images.length > 1 && (
                                <button type="button" className="career-modal-prev" onClick={goToPrevious}>
                                    <i className="fas fa-chevron-left"></i>
                                </button>
                            )}

                            <div className="career-modal-img-container">
                                <div style={{ 
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    padding: zoomLevel === 0 ? '0' : '20px 0'
                                }}>
                                    <img 
                                        src={`${process.env.PUBLIC_URL}/career-assets/${images[selectedIndex]}`}
                                        alt="Full Size Job Ad" 
                                        className="career-modal-img" 
                                        style={
                                            zoomLevel === 0 
                                            ? { maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain', width: 'auto', height: 'auto', transition: 'all 0.3s ease' }
                                            : { width: '100%', height: 'auto', maxWidth: '100%', objectFit: 'contain', transition: 'width 0.3s ease' }
                                        }
                                    />
                                </div>
                            </div>

                            {images.length > 1 && (
                                <button type="button" className="career-modal-next" onClick={goToNext}>
                                    <i className="fas fa-chevron-right"></i>
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Job Application Modal */}
                <ApplyModal 
                    isOpen={applyState.isOpen} 
                    onClose={closeApplyModal} 
                    jobImage={applyState.jobImage}
                    jobIndex={applyState.jobIndex}
                />
            </section>
        </>
    );
}
